import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Building2, Loader2, RefreshCw, CheckCircle, Clock, Eye,
  FileText, Mail, Phone, MapPin, ChevronDown, AlertTriangle
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  filed: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

export function FormationOrdersPanel({ open, onOpenChange }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/corpnet/formation-orders`);
      if (res.ok) setOrders((await res.json()).orders || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) fetchOrders(); }, [open, fetchOrders]);

  const updateStatus = async (orderId, status) => {
    setUpdating(orderId);
    try {
      const res = await authFetch(`${API}/corpnet/formation-orders/${orderId}`, {
        method: "PUT", body: JSON.stringify({ status }),
      });
      if (res.ok) { toast.success(`Order ${orderId} → ${status}`); fetchOrders(); }
      else toast.error("Failed to update");
    } catch { toast.error("Failed"); }
    setUpdating(null);
  };

  if (!open) return null;

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    processing: orders.filter(o => o.status === "processing").length,
    completed: orders.filter(o => ["filed", "completed"].includes(o.status)).length,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="formation-orders-panel">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#2BC40E]" /> Business Formation Orders
          </DialogTitle>
          <DialogDescription>Manage submitted business formation requests</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
          ) : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                <Card className="border-neutral-200"><CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-neutral-900">{stats.total}</p>
                  <p className="text-[10px] text-neutral-500 uppercase">Total</p>
                </CardContent></Card>
                <Card className="border-neutral-200 bg-amber-50"><CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  <p className="text-[10px] text-amber-600 uppercase">Pending</p>
                </CardContent></Card>
                <Card className="border-neutral-200 bg-blue-50"><CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{stats.processing}</p>
                  <p className="text-[10px] text-blue-600 uppercase">Processing</p>
                </CardContent></Card>
                <Card className="border-neutral-200 bg-green-50"><CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                  <p className="text-[10px] text-green-600 uppercase">Completed</p>
                </CardContent></Card>
              </div>

              {orders.length === 0 ? (
                <div className="text-center py-12 text-neutral-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No formation orders yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map(order => {
                    const contact = order.contact || {};
                    const company = order.company_info || {};
                    const expanded = selectedOrder === order.order_id;

                    return (
                      <Card key={order.order_id} className="border-neutral-200" data-testid={`order-${order.order_id}`}>
                        <CardContent className="p-0">
                          {/* Summary Row */}
                          <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setSelectedOrder(expanded ? null : order.order_id)}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm font-bold text-neutral-900">{order.order_id}</span>
                                <Badge className={STATUS_COLORS[order.status] || STATUS_COLORS.pending}>{order.status}</Badge>
                                <Badge variant="outline" className="text-xs">{order.entity_type}</Badge>
                              </div>
                              <p className="text-sm font-medium text-neutral-900 truncate">{company.companyName || "—"}</p>
                              <p className="text-xs text-neutral-500">{contact.firstName} {contact.lastName} • {contact.email} • {order.filing_state}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-neutral-400">{order.created_at?.slice(0, 10)}</p>
                              <ChevronDown className={`w-4 h-4 text-neutral-400 mt-1 transition-transform ${expanded ? "rotate-180" : ""}`} />
                            </div>
                          </div>

                          {/* Expanded Detail */}
                          {expanded && (
                            <div className="border-t border-neutral-200 p-4 bg-neutral-50 space-y-4 text-sm">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <p className="font-semibold text-neutral-900 mb-2">Company Details</p>
                                  <div className="space-y-1 text-neutral-600">
                                    <p><strong>Name:</strong> {company.companyName}</p>
                                    {company.alternativeName && <p><strong>Alt Name:</strong> {company.alternativeName}</p>}
                                    <p><strong>Type:</strong> {order.entity_type} in {order.filing_state}</p>
                                    <p><strong>Filing:</strong> {order.filing_method} {order.s_corp_election ? "• S-Corp Election" : ""}</p>
                                    {company.businessDescription && <p><strong>Description:</strong> {company.businessDescription}</p>}
                                    {company.nonprofitDescription && <p><strong>Purpose:</strong> {company.nonprofitDescription}</p>}
                                    {company.profession && <p><strong>Profession:</strong> {company.profession} (License: {company.licenseNumber})</p>}
                                    {company.totalShares && <p><strong>Shares:</strong> {company.totalShares} @ {company.totalParValue || "N/A"} par</p>}
                                    <p><strong>Address:</strong> {company.businessAddress1} {company.businessAddress2} {company.businessCity} {company.businessState} {company.businessZip}</p>
                                  </div>
                                </div>
                                <div>
                                  <p className="font-semibold text-neutral-900 mb-2">Contact</p>
                                  <div className="space-y-1 text-neutral-600">
                                    <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {contact.email}</p>
                                    {contact.phone && <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {contact.phone}</p>}
                                    <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {contact.address} {contact.city} {contact.state} {contact.zip}</p>
                                  </div>

                                  {order.registered_agent && (
                                    <div className="mt-3">
                                      <p className="font-semibold text-neutral-900 mb-1">Registered Agent</p>
                                      <p className="text-neutral-600">{order.registered_agent.useCorpnet ? "CorpNet RA (1st year free)" : order.registered_agent.name || "Not specified"}</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Members/Shareholders/Directors/Officers */}
                              <div className="grid md:grid-cols-2 gap-4">
                                {order.members?.length > 0 && order.members.some(m => m.name) && (
                                  <div>
                                    <p className="font-semibold text-neutral-900 mb-1">Members</p>
                                    {order.members.filter(m => m.name).map((m, i) => (
                                      <p key={i} className="text-neutral-600 text-xs">{m.name} — {m.ownership || "N/A"} ownership</p>
                                    ))}
                                  </div>
                                )}
                                {order.shareholders?.length > 0 && order.shareholders.some(s => s.name) && (
                                  <div>
                                    <p className="font-semibold text-neutral-900 mb-1">Shareholders</p>
                                    {order.shareholders.filter(s => s.name).map((s, i) => (
                                      <p key={i} className="text-neutral-600 text-xs">{s.name} — {s.ownership || "N/A"} ownership</p>
                                    ))}
                                  </div>
                                )}
                                {order.directors?.length > 0 && order.directors.some(d => d.name) && (
                                  <div>
                                    <p className="font-semibold text-neutral-900 mb-1">Directors</p>
                                    {order.directors.filter(d => d.name).map((d, i) => (
                                      <p key={i} className="text-neutral-600 text-xs">{d.name}</p>
                                    ))}
                                  </div>
                                )}
                                {order.officers && Object.values(order.officers).some(v => v) && (
                                  <div>
                                    <p className="font-semibold text-neutral-900 mb-1">Officers</p>
                                    {Object.entries(order.officers).filter(([, v]) => v).map(([k, v]) => (
                                      <p key={k} className="text-neutral-600 text-xs"><strong>{k.replace(/([A-Z])/g, ' $1').trim()}:</strong> {v}</p>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Status Update */}
                              <div className="flex items-center gap-2 pt-2 border-t border-neutral-200">
                                <span className="text-xs text-neutral-500">Update Status:</span>
                                {["pending", "processing", "filed", "completed", "cancelled"].map(s => (
                                  <Button key={s} size="sm" variant={order.status === s ? "default" : "outline"}
                                    className={`h-7 text-xs ${order.status === s ? "bg-[#333333] text-white" : ""}`}
                                    onClick={() => updateStatus(order.order_id, s)}
                                    disabled={updating === order.order_id}>
                                    {s}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              <div className="text-center mt-4">
                <Button variant="ghost" size="sm" onClick={fetchOrders} className="text-neutral-500">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FormationOrdersPanel;
